import { EditEntitySection } from '@/modules/entities/EditEntitySplitScreen/EditEntitySplitScreen.types';

// The "Current access" card reflects an entity's current-beneficiary subform;
// which edit section that is varies by kind (SLAT tracks current access via its
// lifetime beneficiaries, everything else via the standard beneficiaries view).
const CURRENT_ACCESS_SECTIONS = [
  EditEntitySection.BENEFICIARIES,
  EditEntitySection.LIFETIME_BENEFICIARIES,
];

// The "Dispositions" card is dispositive provisions, except for retirement /
// DH-insurance accounts (under the BDG flag) where it's beneficiary designations.
const DISPOSITIONS_SECTIONS = [
  EditEntitySection.DISPOSITIVE_PROVISIONS_GRANTOR_1,
  EditEntitySection.BENEFICIARY_DESIGNATIONS,
];

/**
 * Pick the first of `candidates` that the entity actually exposes as an edit
 * section. Resolving against the entity's own section list (the same source the
 * edit-entity dropdown uses) keeps the button in lockstep with the modal — e.g.
 * it lands on the BDG view exactly when the dispositions card is in BDG mode.
 */
export function findEditSection(
  availableSections: { section: EditEntitySection }[],
  candidates: EditEntitySection[]
): EditEntitySection | undefined {
  return availableSections.find((option) => candidates.includes(option.section))
    ?.section;
}

/**
 * Resolve the edit-entity sections behind the two access-&-dispositions cards
 * from the entity's available edit sections. Either may be `undefined` when the
 * entity has no matching section (e.g. an insurance account has no current-access
 * section), in which case that card gets no edit toolbar.
 */
export function resolveAccessAndDispositionsSections(
  availableSections: { section: EditEntitySection }[]
): {
  currentAccessSection: EditEntitySection | undefined;
  dispositionsSection: EditEntitySection | undefined;
} {
  return {
    currentAccessSection: findEditSection(
      availableSections,
      CURRENT_ACCESS_SECTIONS
    ),
    dispositionsSection: findEditSection(
      availableSections,
      DISPOSITIONS_SECTIONS
    ),
  };
}

/**
 * Toolbar label for the dispositions card. Tracks the resolved section so the
 * button matches the card heading — "Beneficiary designations" for retirement /
 * DH-insurance accounts (BDG mode), "Dispositions" otherwise.
 */
export function getDispositionsEditLabel(section: EditEntitySection): string {
  return section === EditEntitySection.BENEFICIARY_DESIGNATIONS
    ? 'Edit beneficiary designations'
    : 'Edit dispositions';
}
